/**
 * Menu inicial — a porta de entrada da demonstração.
 *
 * Três destinos: a configuração da política e as duas arquiteturas de voz.
 * O console de supervisão fica como link discreto: ele é um "a mais" que se
 * abre numa segunda janela quando alguém pergunta "e como vocês provam isso?".
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { Logo } from '../components/Logo'
import { api } from '../lib/api'
import { color, radius, shadow } from '../theme/tokens'

interface Entry {
  to: string
  title: string
}

// Só o que a apresentação usa. O Modelo A (/linha) continua existindo como
// reserva, mas fora do menu: o que não entra na narrativa não levanta
// pergunta.
const ENTRIES: Entry[] = [
  { to: '/politica', title: 'Configurações' },
  { to: '/linha-2', title: 'Agente de voz' },
  { to: '/whatsapp', title: 'WhatsApp' },
]

export function Menu() {
  const [keyReady, setKeyReady] = useState<boolean | null>(null)

  useEffect(() => {
    api
      .health()
      .then((health) => setKeyReady(health.realtime_enabled))
      .catch(() => setKeyReady(null))
  }, [])

  return (
    <div
      style={{
        minHeight: '100vh',
        background: color.panel,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{ width: 520, maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Logo size={34} />
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '.01em' }}>
              Hub de Cobrança <span style={{ color: color.brand }}>· IA</span>
            </div>
            <div style={{ fontSize: 12, color: color.panelTextFaint, marginTop: 2 }}>
              Demonstração — Banco Vantagem
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {ENTRIES.map((entry) => (
            <Link
              key={entry.to}
              to={entry.to}
              style={{
                display: 'block',
                textDecoration: 'none',
                color: 'inherit',
                background: '#1b2330',
                border: '1px solid rgba(255,255,255,.09)',
                borderRadius: radius.panel,
                boxShadow: shadow.card,
                padding: '16px 18px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <span style={{ fontSize: 16.5, fontWeight: 600 }}>{entry.title}</span>
                <span aria-hidden style={{ color: color.brand, fontSize: 16 }}>→</span>
              </div>
            </Link>
          ))}
        </div>

        {keyReady === false ? (
          <div
            style={{
              fontSize: 12,
              color: '#ffd9cf',
              background: 'rgba(237,79,46,.12)',
              border: '1px solid rgba(237,79,46,.35)',
              borderRadius: radius.lg,
              padding: '10px 12px',
              lineHeight: 1.5,
            }}
          >
            OPENAI_API_KEY não configurada — o agente de voz e o WhatsApp ficarão
            indisponíveis até defini-la no <code>.env</code> e reiniciar o backend.
          </div>
        ) : null}

        <div style={{ fontSize: 12, color: color.panelTextDim, textAlign: 'center' }}>
          <Link to="/console" style={{ color: color.panelTextMuted }}>
            Console de supervisão
          </Link>{' '}
          — abra numa segunda janela para acompanhar a chamada
          {' · '}
          <Link to="/debug" style={{ color: color.panelTextDim }}>
            telemetria
          </Link>
        </div>
      </div>
    </div>
  )
}
